import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

/**
 * ModifierToken - A draggable token chip representing a trait modifier value
 *
 * Color scheme:
 * - +2: Green (highest bonus)
 * - +1: Blue (moderate bonus)
 * - 0: Gray (neutral)
 * - -1: Red (penalty)
 *
 * Can be dragged from pool OR from a trait slot (to move between slots)
 */
const ModifierToken = ({ value, id, disabled, sourceSlot }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
    data: {
      value,
      sourceSlot // Track where this token is coming from (pool or slot name)
    }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  // Color coding based on value
  const getColorClasses = () => {
    switch (value) {
      case 2:
        return 'bg-gradient-to-br from-green-600 to-green-700 border-green-500 text-white';
      case 1:
        return 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500 text-white';
      case 0:
        return 'bg-gradient-to-br from-gray-600 to-gray-700 border-gray-500 text-white';
      case -1:
        return 'bg-gradient-to-br from-red-600 to-red-700 border-red-500 text-white';
      default:
        return 'bg-gray-600 border-gray-500 text-white';
    }
  };

  // Format value for display (add + sign for positive values)
  const displayValue = value > 0 ? `+${value}` : value;

  // Determine if this token is in a slot (smaller size) or in pool (normal size)
  const inSlot = sourceSlot !== undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        relative flex items-center justify-center
        ${inSlot ? 'w-14 h-14 text-base' : 'w-16 h-16 lg:w-14 lg:h-14 text-lg'}
        rounded-full border-2
        font-bold
        cursor-grab active:cursor-grabbing
        transition-all duration-150
        touch-none
        ${getColorClasses()}
        ${isDragging ? 'opacity-30' : 'opacity-100'}
        ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:scale-105 hover:shadow-lg'}
      `}
      aria-label={`Modifier ${displayValue}${disabled ? ', unavailable' : ''}${inSlot ? `, in ${sourceSlot} slot` : ', in pool'}`}
      aria-disabled={disabled}
    >
      {displayValue}

      {/* Subtle inner glow */}
      <div className="absolute inset-0 rounded-full bg-white opacity-10 pointer-events-none" />
    </div>
  );
};

export default ModifierToken;
